import { action, computed, observable } from "mobx";
import { InstrumentationEventDomEventFired, InstrumentationEventGeolocationRequestSuccess } from "../../lib/Events/InstrumentationEvent";
import { executeEventually } from "../../lib/utils/executeEventually";
import { DbRecordedEvent, UUID } from "../../State/EvidenceDB/DatabaseTypes";
import { DbEventsTableReader } from "../../State/EvidenceDB/DbEventsTableReader";
import { DbRecordingEntryReader } from "../../State/EvidenceDB/DbRecordingEntryReader";
import { NewEventsAddedToDatabase } from "../../State/StateEvents";
import { EvidenceDatabase } from "../../State/EvidenceDB/EvidenceDbStruct";
import { TabUpdatedEvent, TabCreatedEvent } from "../../lib/Events/TabEvents";
import { urlToURL } from "../../lib/utils/domains";
import { commonPathTermsToIgnore } from "../../constants";

const notableMsBetweenInputChangeEvents = 3000;

type TabEvents = TabUpdatedEvent | TabCreatedEvent;

type DomEventFromWhichToGenerateSearchTerm = DbRecordedEvent<InstrumentationEventDomEventFired<"change" | "input" | "paste">> & { raw: { value: string} };
const isDomEventFromWhichToGenerateSearchTerm = (e: DbRecordedEvent<InstrumentationEventDomEventFired>): e is DomEventFromWhichToGenerateSearchTerm  => 
	(e.raw.domOrCustomEventName === "change" || e.raw.domOrCustomEventName === "input" || e.raw.domOrCustomEventName === "paste") &&
	e.raw.value != null && e.raw.value.length > 2;

export class InferrerOfSearchTermsFromDomEvents {
	protected _domEventsFromWhichToGenerateSearchTerms = observable.array<DomEventFromWhichToGenerateSearchTerm>();
	protected _geolocationPositionSuccessEventsObserved = observable.array<InstrumentationEventGeolocationRequestSuccess>();
	protected _tabEventsObserved = observable.array<TabEvents>();
	@computed get domEventsFromWhichToGenerateSearchTerms() { return this._domEventsFromWhichToGenerateSearchTerms; }
	@computed get geolocationPositionSuccessEventsObserved() { return this._geolocationPositionSuccessEventsObserved; }
	@computed get tabEventsObserved() { return this._tabEventsObserved; }

	addDomEventFiredEvents = action ( (...events: DbRecordedEvent<InstrumentationEventDomEventFired>[]) => {
		this._domEventsFromWhichToGenerateSearchTerms.push(...events.filter(isDomEventFromWhichToGenerateSearchTerm));
	});

	addGeolocationSuccessEvents = action( (...events: InstrumentationEventGeolocationRequestSuccess[]) => {
		this._geolocationPositionSuccessEventsObserved.push(...events);
	});

	addTabEvents = action( (...events: TabEvents[]) => {
		this._tabEventsObserved.push(...events);
	});

	processEvent = (event: DbRecordedEvent) => {
		switch (event.eventName) {
			case "DomEventFired":
				this.addDomEventFiredEvents(event);
				break;
			case "GeolocationRequest.getCurrentPosition.success":
			case "GeolocationRequest.watchPosition.success":
				this.addGeolocationSuccessEvents(event);
				break;
			case "updated":
			case "created":
				this.addTabEvents(event);
				break;
			default:
				break;
		}
	};

	@computed get potentialSearchTermEventPairs() {
		const byElementUuid = new Map<string, DomEventFromWhichToGenerateSearchTerm[]>();
		for (const newEvent of this.domEventsFromWhichToGenerateSearchTerms) {
			const priorEventsToKeep = (byElementUuid.get(newEvent.raw.elementUUID) ?? []).filter(
				oldEvent => oldEvent.raw.whenObservedMs < newEvent.raw.whenObservedMs - notableMsBetweenInputChangeEvents
			);
			byElementUuid.set(newEvent.raw.elementUUID, [...priorEventsToKeep, newEvent]);
		}
		const bySearchTerm = new Map<string, DomEventFromWhichToGenerateSearchTerm | InstrumentationEventGeolocationRequestSuccess | TabEvents>();
		for (const events of byElementUuid.values()) {
			for (const event of events) {
				if (!bySearchTerm.has(event.raw.value)) {
					bySearchTerm.set(event.raw.value, event);
				}
			}
		}
		for (const event of this.geolocationPositionSuccessEventsObserved) {
			const {longitude, latitude} = event.raw.position.coords;
			for (const coord of [longitude, latitude]) {
				if (coord != null) {
					// keep 6 digits of precision (one character is "." and one may be '-')
					const coordString = coord.toString().slice(0, coord < 0 ? 8 : 7);					
					bySearchTerm.set(coordString, event);
				}
			}
		}
		for (const event of this.tabEventsObserved) {
			const {url} = event.raw;
			if (url == null || url.length == 0) continue;
			const urlObj = urlToURL(url);
			if (urlObj == null) continue;
			const pathEnd = decodeURIComponent(urlObj.pathname.split('/').pop() ?? '');
			const indexOfDot = pathEnd.indexOf('.');
			if (indexOfDot > 0 && indexOfDot >- pathEnd.length -5) {
				// This sure looks like a file name and extension, so let's not treat it as a potential search term.
				continue;
			}
			const searchTerm = pathEnd
				.replaceAll('_', ' ')
				.replaceAll('-', ' ');
			if (searchTerm.indexOf(' ') === -1) {
				// This is a single word and so probably not a search term worth inferring.
				continue;
			}
			if (pathEnd.replaceAll(' ', '').length >= 6) {
				if (!bySearchTerm.has(searchTerm)) {
					bySearchTerm.set(searchTerm, event);
				}
			}
		}
		return [...bySearchTerm.entries()]
	};

}

export class DbInferrerOfSearchTermsFromDomEvents extends InferrerOfSearchTermsFromDomEvents{
	readonly eventsTableReader: DbEventsTableReader;

	initDomEventFiredEvents = executeEventually( async () => {
		const domEventFiredEvents = await this.eventsTableReader.getEventsForCategoryAndName({category: "Instrumentation", name: "DomEventFired", startingAtIndexInclusive: 0});
		this.addDomEventFiredEvents(...domEventFiredEvents);
	});

	initGeolocationWatchPositionSuccessEvents = executeEventually( async () => {
		const geolocationSuccessEvents = ([] as DbRecordedEvent<InstrumentationEventGeolocationRequestSuccess>[]).concat(
			...await Promise.all([
				this.eventsTableReader.getEventsForCategoryAndName({category: "Instrumentation", name: `GeolocationRequest.getCurrentPosition.success`, startingAtIndexInclusive: 0}),
				this.eventsTableReader.getEventsForCategoryAndName({category: "Instrumentation", name: `GeolocationRequest.watchPosition.success`, startingAtIndexInclusive: 0}),
			])
		).sort( (a, b) => a.eventIndex - b.eventIndex);
		this.addGeolocationSuccessEvents(...geolocationSuccessEvents);
	});

	initTabEvents = executeEventually( async () => {
		const tabEvents = ([] as DbRecordedEvent<TabEvents>[]).concat(
			...await Promise.all([
				this.eventsTableReader.getEventsForCategoryAndName({category: 'Tabs', name: `updated`, startingAtIndexInclusive: 0}),
				this.eventsTableReader.getEventsForCategoryAndName({category: 'Tabs', name: `created`, startingAtIndexInclusive: 0}),
			])
		).sort( (a, b) => a.eventIndex - b.eventIndex);
		this.addTabEvents(...tabEvents);
	});

	

	@computed get searchTermEventPairsChronological() {
		const searchTermsToIgnore = new Set<string>([...commonPathTermsToIgnore,
			...(DbRecordingEntryReader.fromRecordingUuid(this.recordingUuid)?.recording?.domGeneratedSearchTermsToIgnore ?? [])
		]);
		return this.potentialSearchTermEventPairs
			.filter( ([searchTerm]) => !searchTermsToIgnore.has(searchTerm) && !searchTermsToIgnore.has(searchTerm.toLocaleLowerCase()) && searchTerm.length > 3 );
	};

	@computed get searchTermsIncludeByDefaultFromDomEventsChronological(): [string, boolean][] {
		return this.searchTermEventPairsChronological.map( ([searchTerm, event]) => [searchTerm, event.category === 'Instrumentation']);
	}

	@computed get searchTermsFromDomEventsSortedAlphanumeric() {
		return this.searchTermsIncludeByDefaultFromDomEventsChronological.sort( ([a], [b]) => a.localeCompare(b));
	}

	constructor(
		protected readonly db: EvidenceDatabase,
		public readonly recordingUuid: UUID,
	) {
		super();
		this.eventsTableReader = DbEventsTableReader.forDbAndRecordingUuid(db, recordingUuid);
		NewEventsAddedToDatabase.addListener( async e => {
			const {recordingUuid: _, eventsInfo} = e;
			for (const e of eventsInfo) {
				switch (e.eventName) {
					case "created":
					case "updated":
					case "DomEventFired":
					case "GeolocationRequest.getCurrentPosition.success":
					case "GeolocationRequest.watchPosition.success": {
							const {eventIndex} = e;
							const event = await this.eventsTableReader.getEvent({eventIndex});
							if (event == null) return;
							this.processEvent(event);
						}
						break;
					default:
						break;
				}
			}
		});
		this.initDomEventFiredEvents();
		this.initGeolocationWatchPositionSuccessEvents();
		this.initTabEvents();
	}




}
